import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SelectedOption {
  groupName: string;
  choiceName: string;
  price: number;
  quantity: number;
}

interface CartItem {
  product_id: number;
  name: string;
  price: number;
  image?: string;
  quantity: number;
  selectedOptions?: SelectedOption[];
  notes?: string;
  // unique key for items with different options
  cartKey: string;
}

interface CartState {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity' | 'cartKey'>) => void;
  removeItem: (cartKey: string) => void;
  updateQuantity: (cartKey: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
}

function buildCartKey(productId: number, selectedOptions?: SelectedOption[]): string {
  if (!selectedOptions?.length) return `p_${productId}`;
  const optKey = selectedOptions
    .map(o => `${o.groupName}:${o.choiceName}:${o.quantity}`)
    .sort()
    .join('|');
  return `p_${productId}_${optKey}`;
}

function getOptionsTotal(options?: SelectedOption[]): number {
  if (!options?.length) return 0;
  return options.reduce((sum, o) => sum + o.price * o.quantity, 0);
}

export const useCartStore = create<CartState>()(persist((set, get) => ({
  items: [],

  addItem: (item) => {
    const cartKey = buildCartKey(item.product_id, item.selectedOptions);
    set((state) => {
      const existing = state.items.find((i) => i.cartKey === cartKey);
      if (existing) {
        return {
          items: state.items.map((i) =>
            i.cartKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i
          ),
        };
      }
      return { items: [...state.items, { ...item, quantity: 1, cartKey }] };
    });
  },

  removeItem: (cartKey) => {
    set((state) => ({
      items: state.items.filter((i) => i.cartKey !== cartKey),
    }));
  },

  updateQuantity: (cartKey, quantity) => {
    if (quantity <= 0) {
      get().removeItem(cartKey);
      return;
    }
    set((state) => ({
      items: state.items.map((i) =>
        i.cartKey === cartKey ? { ...i, quantity } : i
      ),
    }));
  },

  clearCart: () => set({ items: [] }),

  getTotal: () => {
    return get().items.reduce((sum, item) => {
      const optTotal = getOptionsTotal(item.selectedOptions);
      return sum + (item.price + optTotal) * item.quantity;
    }, 0);
  },

  getItemCount: () => {
    return get().items.reduce((sum, item) => sum + item.quantity, 0);
  },
}), { name: 'cartz-cart' }));
